import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { HeartHandshake, UserPlus, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  open: "bg-forest/10 text-forest",
  fulfilled: "bg-forest text-white",
  expired: "bg-neutral-200 text-neutral-600",
  invited: "bg-amber-100 text-amber-800",
  joined: "bg-forest/10 text-forest",
  first_action: "bg-forest text-white",
};
const STATUS_LABELS = {
  open: "Open", fulfilled: "Helper confirmed", expired: "Expired",
  invited: "Invited", joined: "Joined", first_action: "First action taken",
};

function StatusBadge({ s }) {
  return <Badge className={`border-none text-[11px] rounded-full ${STATUS_STYLES[s] || ""}`}>{STATUS_LABELS[s] || s}</Badge>;
}

// Phase 18-A2 item 2: the referrer's dashboard section. Type A = mediated
// help request (consent-gated, first name + outward postcode ONLY). Type B =
// personal invite link with per-link status and nothing else.
export function ReferralsPanel() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_first_name: "", outward_postcode: "", need_category: "", description: "", availability: "" });
  const [consent, setConsent] = useState(false);
  const [inviteArea, setInviteArea] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.myReferrals().then(setData).catch(() => {});
    api.referrerMe().then((r) => setCats(r.need_categories || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitReferral = async () => {
    setBusy(true);
    try {
      await api.createReferral({ ...form, consent });
      setOpen(false);
      setForm({ client_first_name: "", outward_postcode: "", need_category: "", description: "", availability: "" });
      setConsent(false);
      toast.success("Request posted — offers will come to you.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post the request");
    }
    setBusy(false);
  };

  const makeInvite = async () => {
    setBusy(true);
    try {
      const inv = await api.createReferralInvite({ area: inviteArea });
      setInviteArea("");
      // clipboard can be blocked outside a direct gesture — say what really happened
      try {
        await navigator.clipboard.writeText(inv.link);
        toast.success("Invite link created and copied");
      } catch {
        toast.success("Invite link created — use Copy to grab it");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the link");
    }
    setBusy(false);
  };

  const repost = async (id) => {
    try {
      await api.repostReferral(id);
      toast.success("Reposted for another 30 days");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not repost"); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success("Link copied")).catch(() => {});
  };

  if (!data) return null;

  return (
    <div className="mt-14" data-testid="referrals-panel">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <HeartHandshake className="w-5 h-5 text-terracotta" /> My referrals
      </h2>
      <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
        Refer someone who needs a hand, or invite someone who'd benefit from
        volunteering. Offers and messages always come to you.
      </p>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button data-testid="refer-someone-btn" onClick={() => setOpen(true)} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-5 text-sm">
          <HeartHandshake className="w-4 h-4 mr-1.5" /> Refer someone
        </Button>
        <div className="flex items-center gap-2">
          <Input data-testid="invite-area-input" value={inviteArea} onChange={(e) => setInviteArea(e.target.value)} placeholder="Area (optional)" className="h-9 w-44 text-sm" />
          <Button data-testid="create-invite-btn" onClick={makeInvite} disabled={busy} variant="outline" className="rounded-full h-9 px-5 text-sm">
            <UserPlus className="w-4 h-4 mr-1.5" /> Create invite link
          </Button>
        </div>
      </div>

      {data.referrals.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="referrals-list">
          {data.referrals.map((r) => (
            <div key={r.id} data-testid={`referral-row-${r.id}`} className="flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-medium text-forest truncate">{r.need_category} for {r.client_first_name} · {r.outward_postcode}</div>
                <div className="text-xs text-neutral-500">
                  {r.offers} offer{r.offers !== 1 ? "s" : ""} · expires {(r.expires_at || "").slice(0, 10)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge s={r.status} />
                {r.status === "expired" && (
                  <Button data-testid={`repost-btn-${r.id}`} onClick={() => repost(r.id)} variant="outline" className="rounded-full h-8 px-3 text-xs">
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Repost
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {data.invites.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="invites-list">
          {data.invites.map((inv) => (
            <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-medium text-forest">Invite {inv.code}{inv.area ? ` · ${inv.area}` : ""}</div>
                <div className="text-xs text-neutral-500 truncate max-w-md">{inv.link}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge s={inv.status} />
                <Button onClick={() => copyLink(inv.link)} variant="outline" className="rounded-full h-8 px-3 text-xs" data-testid={`copy-invite-${inv.id}`}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="referral-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Refer someone who needs a hand</DialogTitle>
            <DialogDescription>
              You stay the point of contact — offers come to you, and you
              choose who helps.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="referral-first-name" placeholder="Client first name only" value={form.client_first_name} onChange={set("client_first_name")} />
              <Input data-testid="referral-outward" placeholder="Outward postcode, e.g. SE15" value={form.outward_postcode} onChange={set("outward_postcode")} />
            </div>
            <select
              data-testid="referral-category"
              value={form.need_category}
              onChange={set("need_category")}
              className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
            >
              <option value="">— what do they need? —</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div>
              <Textarea data-testid="referral-description" rows={3} placeholder="What needs doing, practically?" value={form.description} onChange={set("description")} />
              <p className="text-[11px] text-amber-700 mt-1" data-testid="referral-guidance">
                Describe the practical task only — no health or personal details.
              </p>
            </div>
            <Input data-testid="referral-availability" placeholder="Availability (e.g. weekday mornings)" value={form.availability} onChange={set("availability")} />
            <label className="flex items-start gap-2.5 text-sm text-neutral-700 cursor-pointer">
              <Checkbox data-testid="referral-consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
              <span>I have this person's consent to post this request.</span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="referral-submit" onClick={submitReferral} disabled={busy || !consent} className="rounded-full bg-forest hover:bg-forest-hover">
                {busy ? "Posting…" : "Post request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
